import { IconPlus } from '@iwoca/icons';
import { Card, CheckBox, ErrorMessage } from '@iwoca/orion';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { useFormContext } from 'react-hook-form';
import signupFormStyles from '../SignupForm/SignupForm.module.css';
import { TSignupFormData } from '../SignupForm/SignupForm.types';
import { DebtCard } from './DebtCard/DebtCard';
import { DebtSummary } from './DebtSummary/DebtSummary';
import { DebtTabs } from './DebtTabs/DebtTabs';
import styles from './RefinancingDebts.module.css';
import { useRefinancingDebts } from './useRefinancingDebts.hook';

type TProps = {
  disabled?: boolean;
};

export const RefinancingDebts = ({ disabled }: TProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<TSignupFormData>();
  const confirmationErrors = errors.refinancing;
  const debtHasError = (index: number) =>
    Boolean(errors.refinancing?.debts?.[index]);
  const {
    debts,
    targetDebtIndex,
    setTargetDebtIndex,
    addDebt,
    removeDebt,
    canAddDebt,
    requestedAmount,
    totalDebt,
    amountRemaining,
    debtLines,
  } = useRefinancingDebts();

  return (
    <Card className={signupFormStyles.formSection}>
      <h2>Debts to be refinanced</h2>
      <div className={signupFormStyles.cardContents}>
        <div className={styles.container}>
          <DebtSummary
            requestedAmount={requestedAmount}
            totalDebt={totalDebt}
            remaining={amountRemaining}
            debts={debtLines}
          />
          <DebtTabs
            debts={debts}
            targetDebtIndex={targetDebtIndex}
            setTargetDebtIndex={setTargetDebtIndex}
            debtHasError={debtHasError}
          />
          {/*
            Keep every debt mounted and toggle visibility with `hidden` rather
            than conditionally rendering the active tab — this preserves each
            DebtCard's react-hook-form field state when switching tabs.
          */}
          {debts.map((debt, index) => (
            <div key={debt.id} hidden={index !== targetDebtIndex}>
              <DebtCard
                index={index}
                showHeader={debts.length > 1}
                canRemove={index > 0}
                onRemove={() => removeDebt(index)}
                disabled={disabled}
              />
            </div>
          ))}
          {canAddDebt && (
            <ButtonLink
              type="button"
              icon={IconPlus}
              iconPosition="left"
              className={styles.addDebt}
              onClick={addDebt}
              disabled={disabled}
            >
              Add another debt
            </ButtonLink>
          )}
          <div className={styles.confirmations}>
            <div className={styles.confirmationField}>
              <CheckBox
                {...register('refinancing.usesLoanToClearDebts')}
                label="I confirm my client will use this loan to clear the listed debts."
                wrapperClassName={styles.confirmation}
                disabled={disabled}
              />
              {confirmationErrors?.usesLoanToClearDebts?.message && (
                <ErrorMessage>
                  {confirmationErrors.usesLoanToClearDebts.message}
                </ErrorMessage>
              )}
            </div>
            <div className={styles.confirmationField}>
              <CheckBox
                {...register('refinancing.hasEnteredAllDebts')}
                label="I've entered all debts to be refinanced and understand the list can't be changed after iwoca makes a decision."
                wrapperClassName={styles.confirmation}
                disabled={disabled}
              />
              {confirmationErrors?.hasEnteredAllDebts?.message && (
                <ErrorMessage>
                  {confirmationErrors.hasEnteredAllDebts.message}
                </ErrorMessage>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
